import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Check, Compass, Mountain, Users, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface PlanSelectionStepProps {
  onNext: () => void;
}

export const PlanSelectionStep = ({ onNext }: PlanSelectionStepProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>("adventurer"); // Default to adventurer during beta

  const handleBetaEnroll = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke('grant-beta-access', {
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
      });

      if (error) throw error;

      toast({
        title: "Beta Access Activated!",
        description: "You now have Adventurer features.",
      });

      onNext();
    } catch (error) {
      console.error('Beta enrollment error:', error);
      toast({
        title: "Error",
        description: "Unable to activate beta access. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    if (selectedPlan === "adventurer") {
      handleBetaEnroll();
    } else {
      onNext();
    }
  };

  const plans = [
    {
      name: "Scout",
      key: "scout",
      price: "Free",
      period: "",
      icon: <Compass className="w-5 h-5" />,
      description: "Free tier - great for getting started",
      features: [
        "Explore trails and map",
        "Log public runs",
        "Basic activity feed",
        "Join events"
      ],
    },
    {
      name: "Adventurer",
      key: "adventurer",
      price: "Free",
      period: " (Beta)",
      icon: <Mountain className="w-5 h-5" />,
      description: "Full access during beta",
      features: [
        "Everything in Scout",
        "Private run visibility",
        "View community runs",
        "Track editing",
        "Enhanced insights"
      ],
      popular: true,
      betaHighlight: true,
    },
    {
      name: "Organizer",
      key: "organizer",
      price: "Coming",
      period: " Soon",
      icon: <Users className="w-5 h-5" />,
      description: "Create events and lead rides",
      features: [
        "Everything in Adventurer",
        "Create events",
        "Manage clubs",
        "Priority support"
      ],
      comingSoon: true,
    },
  ];

  return (
    <Card>
      <CardHeader className="text-center">
        <Badge className="mx-auto mb-2 bg-primary/10 text-primary border-primary/20">
          🎉 Beta Access
        </Badge>
        <CardTitle className="text-2xl">Choose Your Plan</CardTitle>
        <CardDescription>
          Get full Adventurer access free during our beta period!
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {plans.map((plan) => (
          <div
            key={plan.key}
            onClick={() => !plan.comingSoon && setSelectedPlan(plan.key)}
            className={`relative rounded-lg border p-4 transition-all ${
              selectedPlan === plan.key 
                ? 'border-primary ring-2 ring-primary bg-primary/5' 
                : 'border-border hover:border-primary/50'
            } ${plan.comingSoon ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {selectedPlan === plan.key && !plan.comingSoon && (
              <div className="absolute top-4 right-4">
                <Check className="w-5 h-5 text-primary" />
              </div>
            )}
            {plan.comingSoon && (
              <Badge variant="secondary" className="absolute -top-2.5 right-4">
                Coming Soon
              </Badge>
            )}
            {plan.betaHighlight && !plan.comingSoon && (
              <Badge className="absolute -top-2.5 right-4 bg-primary text-primary-foreground">
                Recommended
              </Badge>
            )}
            
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${plan.popular ? 'bg-primary/10' : 'bg-muted'}`}>
                  {plan.icon}
                </div>
                <div>
                  <h3 className="font-semibold">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xl font-bold">{plan.price}</span>
                <span className="text-sm text-muted-foreground">{plan.period}</span>
              </div>
            </div>
            
            <ul className="mt-3 grid grid-cols-2 gap-1">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="pt-4">
          <Button 
            variant="hero"
            onClick={handleContinue}
            disabled={isLoading}
            className="w-full"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Activating...
              </>
            ) : (
              <>
                {selectedPlan === "scout" && <Compass className="w-4 h-4 mr-2" />}
                {selectedPlan === "adventurer" && <Mountain className="w-4 h-4 mr-2" />}
                {selectedPlan === "adventurer" 
                  ? "Activate Free Beta Access" 
                  : "Continue with Scout"}
              </>
            )}
          </Button>
        </div>
        
        <p className="text-xs text-center text-muted-foreground">
          Beta access is free. We'll notify you before any pricing changes.
        </p>
      </CardContent>
    </Card>
  );
};
